/**
 * PaymentAnalytics — payment-status breakdown for the payments correlated with
 * the current artifact or execution. Calls GetPaymentAnalytics (POST
 * /payments/analytics) with the entity's correlationTags, region-pinned, and
 * renders the server-computed totalPayments + per-status counts. Owns its own
 * fetch so each detail page just passes { correlationTags, region }.
 *
 * Two views, toggled from the header: a Summary (the total plus a per-status
 * number grid) and a Chart (a donut of the status mix with the total centred).
 */
import { useEffect, useState } from 'react';
import Box from '@cloudscape-design/components/box';
import ColumnLayout from '@cloudscape-design/components/column-layout';
import Container from '@cloudscape-design/components/container';
import Header from '@cloudscape-design/components/header';
import PieChart from '@cloudscape-design/components/pie-chart';
import SegmentedControl from '@cloudscape-design/components/segmented-control';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Spinner from '@cloudscape-design/components/spinner';
import StatusIndicator, { StatusIndicatorProps } from '@cloudscape-design/components/status-indicator';
import { useAppDispatch, useAppSelector } from 'src/store/hooks';
import {
  fetchPaymentAnalytics, clearPaymentAnalytics,
} from 'src/store/slices/paymentAnalytics/paymentAnalyticsSlice';

/** Preferred display order; any status the API adds later is appended after these. */
const STATUS_ORDER = ['APPROVED', 'ACCEPTED', 'PENDING', 'REISSUED', 'RETURNED', 'REJECTED', 'UNKNOWN'];

/** paymentStatus -> StatusIndicator colour; UNKNOWN (and anything unmapped) stays plain text. */
const STATUS_TYPE: Record<string, StatusIndicatorProps.Type> = {
  APPROVED: 'success',
  ACCEPTED: 'success',
  PENDING: 'pending',
  REISSUED: 'info',
  RETURNED: 'warning',
  REJECTED: 'error',
};

/** Donut segment colours, aligned with the StatusIndicator semantics above. */
const STATUS_COLOR: Record<string, string> = {
  APPROVED: '#037f0c',
  ACCEPTED: '#2bb534',
  PENDING: '#656871',
  REISSUED: '#0972d3',
  RETURNED: '#e07a00',
  REJECTED: '#d91515',
  UNKNOWN: '#b4b4bb',
};

export const PaymentAnalytics = (
  { correlationTags, region }: { correlationTags: string[]; region: string | null },
) => {
  const dispatch = useAppDispatch();
  const { totalPayments, statusCounts, loading, error } = useAppSelector(state => state.paymentAnalytics);
  const [view, setView] = useState<'summary' | 'chart'>('summary');

  // Fetch whenever the correlated entity (its tags) or region changes.
  useEffect(() => {
    if (!region || correlationTags.length === 0) return undefined;
    const promise = dispatch(fetchPaymentAnalytics({ correlationTags, region }));
    return () => { promise.abort(); };
  }, [correlationTags, region, dispatch]);

  // Drop the slice on unmount so a return visit starts clean.
  useEffect(() => () => { dispatch(clearPaymentAnalytics()); }, [dispatch]);

  // Known statuses first (in order), then any extra keys the API returns.
  const statuses = [
    ...STATUS_ORDER.filter(s => s in statusCounts),
    ...Object.keys(statusCounts).filter(s => !STATUS_ORDER.includes(s)),
  ];
  const ready = correlationTags.length > 0 && !loading && !error;

  const summaryView = (
    <SpaceBetween size="l">
      {/* Total on its own line. */}
      <div>
        <Box variant="awsui-key-label">Total payments</Box>
        <Box variant="awsui-value-large">{totalPayments.toLocaleString()}</Box>
      </div>
      {/* Divider so the total reads as its own section, apart from the status breakdown. */}
      <div style={{ borderTop: '1px solid var(--color-border-divider-default, #e9ebed)' }} />
      <ColumnLayout columns={4} variant="text-grid" borders="vertical">
        {statuses.map(status => (
          <div key={status}>
            <Box variant="awsui-key-label">
              {STATUS_TYPE[status]
                ? <StatusIndicator type={STATUS_TYPE[status]}>{status}</StatusIndicator>
                : status}
            </Box>
            <Box variant="awsui-value-large">{statusCounts[status].toLocaleString()}</Box>
          </div>
        ))}
      </ColumnLayout>
    </SpaceBetween>
  );

  const chartView = (
    <PieChart
      variant="donut"
      size="medium"
      data={statuses
        .filter(status => statusCounts[status] > 0)
        .map(status => ({ title: status, value: statusCounts[status], color: STATUS_COLOR[status] }))}
      innerMetricValue={totalPayments.toLocaleString()}
      innerMetricDescription="Payments"
      hideFilter
      ariaLabel="Payment status breakdown"
      ariaDescription="Donut chart of payment counts by status."
      segmentDescription={(segment) => `${segment.value.toLocaleString()} payments`}
      detailPopoverContent={(segment) => [{ key: 'Payments', value: segment.value.toLocaleString() }]}
      empty={<Box textAlign="center" color="text-body-secondary">No payments to chart.</Box>}
    />
  );

  const renderBody = () => {
    if (correlationTags.length === 0) {
      return <Box color="text-body-secondary">No correlation tags available for payment analytics.</Box>;
    }
    if (loading) {
      return <Box textAlign="center" padding="m"><Spinner /></Box>;
    }
    if (error) {
      return <Box color="text-status-error">{error}</Box>;
    }
    return view === 'chart' ? chartView : summaryView;
  };

  return (
    <Container
      header={(
        <Header
          variant="h2"
          actions={ready ? (
            <SegmentedControl
              selectedId={view}
              onChange={({ detail }) => setView(detail.selectedId as 'summary' | 'chart')}
              label="Analytics view"
              options={[
                { id: 'summary', text: 'Summary', iconName: 'view-full' },
                { id: 'chart', text: 'Chart', iconName: 'suggestions' },
              ]}
            />
          ) : undefined}
        >
          Payment analytics
        </Header>
      )}
    >
      {renderBody()}
    </Container>
  );
};
